import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ResponsiveScatterPlot } from "@nivo/scatterplot";
import { FactionIcon } from "../../../components/icons/FactionIcon";

function computeLinearRegression(points) {
  const n = points.length;
  if (n < 2) return null;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

const NODE_SIZE = 34;

function CustomNode({ node }) {
  const r = NODE_SIZE / 2;
  const { name, icon, nodeColor } = node.data;

  return (
    <g transform={`translate(${node.x}, ${node.y})`}>
      <circle
        r={r}
        cx={0}
        cy={0}
        fill={nodeColor}
        fillOpacity={0.15}
        stroke={nodeColor}
        strokeWidth={2}
      />
      <foreignObject
        x={-r + 3}
        y={-r + 3}
        width={(r - 3) * 2}
        height={(r - 3) * 2}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <FactionIcon
            icon={icon}
            name={name}
            size={Math.round(r * 1.3)}
            sx={{
              backgroundColor: "transparent",
              color: nodeColor
            }}
          />
        </Box>
      </foreignObject>
    </g>
  );
}

export default function DashboardScatterChart({ label, data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const xLabel = data?.x?.label || "";
  const yLabel = data?.y?.label || "";
  const safeRows = data?.rows || [];
  const isPercentageAxis =
    data?.x?.type?.toLowerCase() === "percentage" || /%|rate/i.test(xLabel);

  const avgX =
    safeRows.length > 0
      ? safeRows.reduce((sum, r) => sum + r.x, 0) / safeRows.length
      : 0;

  const getNodeColor = (x) => {
    if (Math.abs(x - avgX) < 1) return isDark ? "#9ca3af" : "#6b7280";
    if (x > avgX) return isDark ? "#4ade80" : "#16a34a";
    return isDark ? "#f87171" : "#dc2626";
  };

  const chartMinWidth = Math.max(500, safeRows.length * 55);

  const allPoints = safeRows.map((row) => ({ x: row.x, y: row.y }));
  const regression = useMemo(
    () => computeLinearRegression(allPoints),
    [allPoints]
  );

  const scatterData = safeRows.map((row) => ({
    id: row.label?.id || row.label?.name,
    data: [
      {
        x: row.x,
        y: row.y,
        name: row.label?.name,
        icon: row.label?.icon,
        nodeColor: getNodeColor(row.x)
      }
    ]
  }));

  const xValues = allPoints.map((p) => p.x);
  const yValues = allPoints.map((p) => p.y);

  const xPadding = isPercentageAxis
    ? 1
    : Math.max(1, (Math.max(...xValues, 0) - Math.min(...xValues, 0)) * 0.05);
  const xMin =
    xValues.length > 0
      ? isPercentageAxis
        ? Math.max(0, Math.min(...xValues) - xPadding)
        : Math.min(...xValues) - xPadding
      : 0;
  const xMax =
    xValues.length > 0
      ? isPercentageAxis
        ? Math.min(100, Math.max(...xValues) + xPadding)
        : Math.max(...xValues) + xPadding
      : 100;

  const maxY = yValues.length > 0 ? Math.max(...yValues) : 0;
  const yStep =
    maxY > 30000
      ? 10000
      : maxY > 3000
        ? 1000
        : maxY > 300
          ? 500
          : maxY > 100
            ? 100
            : maxY > 50
              ? 10
              : 5;
  const yMax =
    yValues.length > 0 ? Math.ceil((maxY * 1.15) / yStep) * yStep : 1000;

  const trendLineLayer = useMemo(
    () =>
      ({ xScale, yScale }) => {
        if (!regression || xValues.length < 2) return null;
        const xMinVal = Math.min(...xValues);
        const xMaxVal = Math.max(...xValues);
        return (
          <line
            x1={xScale(xMinVal)}
            y1={yScale(regression.slope * xMinVal + regression.intercept)}
            x2={xScale(xMaxVal)}
            y2={yScale(regression.slope * xMaxVal + regression.intercept)}
            stroke={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}
            strokeWidth={2}
            strokeLinecap="round"
          />
        );
      },
    [regression, xValues, isDark]
  );

  const layers = useMemo(
    () => ["grid", "axes", trendLineLayer, "nodes", "mesh"],
    [trendLineLayer]
  );

  const nivoTheme = {
    background: "transparent",
    axis: {
      domain: {
        line: {
          stroke: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
          strokeWidth: 1
        }
      },
      ticks: {
        line: {
          stroke: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
          strokeWidth: 1
        },
        text: {
          fill: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.5)",
          fontSize: 11
        }
      },
      legend: {
        text: {
          fill: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
          fontSize: 11,
          fontWeight: 600
        }
      }
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
        strokeWidth: 1
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: 1,
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)"
      }}
    >
      {label?.length > 0 && (
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            color: "primary",
            fontWeight: 600,
            textAlign: "center",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            pt: 2
          }}
        >
          {label}
        </Typography>
      )}

      {safeRows.length === 0 ? null : (
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
            <Box sx={{ minWidth: chartMinWidth, height: { xs: 360, sm: 480 } }}>
              <ResponsiveScatterPlot
                data={scatterData}
                theme={nivoTheme}
                margin={{ top: 20, right: 30, bottom: 60, left: 70 }}
                xScale={{ type: "linear", min: xMin, max: xMax }}
                yScale={{ type: "linear", min: 0, max: yMax }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: xLabel,
                  legendOffset: 46,
                  legendPosition: "middle",
                  format: (v) => (isPercentageAxis ? `${v}%` : v)
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: yLabel,
                  legendOffset: -56,
                  legendPosition: "middle"
                }}
                nodeSize={NODE_SIZE}
                colors={{ scheme: "nivo" }}
                nodeComponent={CustomNode}
                tooltip={({ node }) => (
                  <Box
                    sx={{
                      backgroundColor: isDark ? "#1e2a3a" : "#fff",
                      color: theme.palette.text.primary,
                      p: 1.5,
                      borderRadius: 1,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                      minWidth: 140
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}
                    >
                      {node.data.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        fontSize: "0.8rem"
                      }}
                    >
                      <Box sx={{ color: theme.palette.text.secondary }}>
                        {xLabel}
                      </Box>
                      <Box sx={{ fontWeight: 600 }}>
                        {node.data.x}
                        {isPercentageAxis ? "%" : ""}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        fontSize: "0.8rem"
                      }}
                    >
                      <Box sx={{ color: theme.palette.text.secondary }}>
                        {yLabel}
                      </Box>
                      <Box sx={{ fontWeight: 600 }}>
                        {node.data.y.toLocaleString()}
                      </Box>
                    </Box>
                  </Box>
                )}
                layers={layers}
                useMesh={true}
                enableGridX={true}
                enableGridY={true}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
